import type { UpdateState } from '@hexkeep/shared/domains/licensing';

/**
 * Optional Electron bridge (exposed by apps/desktop preload). Everything must also work in a
 * plain browser, so always feature-check: `hexkeepNative?.openDisplay(...)`.
 */
export interface ScreenInfo {
  id: number;
  label: string;
  bounds: { x: number; y: number; width: number; height: number };
  scaleFactor: number;
  primary: boolean;
}

export interface HexkeepNative {
  platform: string;
  version: string;
  listScreens(): Promise<ScreenInfo[]>;
  openDisplay(displayId: string, screenId?: number | null): Promise<void>;
  closeDisplay(displayId: string): Promise<void>;
  isDisplayOpen(displayId: string): Promise<boolean>;
  pickFile(opts: {
    title?: string;
    filters?: { name: string; extensions: string[] }[];
  }): Promise<string | null>;
  pickSavePath(opts: {
    title?: string;
    defaultPath?: string;
    filters?: { name: string; extensions: string[] }[];
  }): Promise<string | null>;
  /** Folder dialog (art-pack import). Optional: older desktop builds don't expose it. */
  pickFolder?(opts: { title?: string }): Promise<string | null>;
  revealInFolder(path: string): Promise<void>;
  openExternal(url: string): Promise<void>;
  /** App/runtime versions and where data + logs live (desktop app only). */
  appInfo?(): Promise<NativeAppInfo>;
  /** Open the data folder (%APPDATA%\Fablekeep) in the file manager. */
  openDataFolder?(): Promise<void>;
  /** Reveal the log file (<data>/logs/fablekeep.log) in the file manager. */
  openLogs?(): Promise<void>;
  /**
   * Windows Firewall rule for the server port (Settings → Network). `allow` asks the DM in a native
   * dialog, then Windows asks for elevation. Optional: older desktop builds don't expose it.
   */
  /** Licence-gated auto-updates (Settings → Licence). Optional: older desktop builds don't expose it. */
  updates?: {
    state(): Promise<UpdateState | null>;
    check(): Promise<UpdateState | null>;
    /** Quit and install a downloaded update; false when nothing is downloaded. */
    install(): Promise<boolean>;
    /** Subscribe to state changes; returns the unsubscribe function. */
    onChange(cb: (state: UpdateState) => void): () => void;
  };
  /** The DM app is on screen: the desktop app fades the startup tome out (D-311). */
  appReady?(): void;
  /** Desktop-only tome preferences ("Show the tome at startup"; the language the splash uses). */
  tome?: {
    get(): Promise<{ enabled: boolean }>;
    set(prefs: { enabled?: boolean; lang?: string }): Promise<{ enabled: boolean }>;
  };
  /** Splash window only: skip the startup tome. */
  skipSplash?(): void;
  /** Splash only: the app finished loading, so a skip now goes through. */
  onSplashReady?(cb: () => void): void;
  firewall?: {
    status(): Promise<FirewallState>;
    allow(): Promise<FirewallState>;
    remove(): Promise<FirewallState>;
  };
}

export type FirewallState = 'allowed' | 'missing' | 'unsupported' | 'unknown';

export interface NativeAppInfo {
  version: string;
  electron: string;
  chrome: string;
  node: string;
  platform: string;
  arch: string;
  packaged: boolean;
  dataDir: string;
  logFile: string;
}

declare global {
  interface Window {
    hexkeepNative?: HexkeepNative;
  }
}

export const hexkeepNative: HexkeepNative | undefined =
  typeof window !== 'undefined' ? window.hexkeepNative : undefined;
export const isElectron = !!hexkeepNative;
